import React from "react";
import "./FlowSelectModal.css";

// Shared visual helpers so the flow picker looks identical in single-contact and
// bulk mode. Kept in sync with FlowSelectModal's rich "idle" picker.
const getStepIcon = (step) => {
  const t = step?.type || step?.nodeType || '';
  if (t === 'email_sent' || t.includes('email')) return 'fa fa-paper-plane';
  if (t.includes('instagram')) return 'fab fa-instagram';
  if (t.includes('linkedin')) return 'fab fa-linkedin';
  if (t.includes('whatsapp')) return 'fab fa-whatsapp';
  if (t.includes('phone') || t.includes('call')) return 'fa fa-phone-alt';
  if (t === 'delay' || step?.nodeType === 'delay') return 'fa fa-hourglass-half';
  if (t === 'condition' || step?.nodeType === 'condition') return 'fa fa-code-branch';
  if (t === 'end' || step?.nodeType === 'end') return 'fa fa-flag-checkered';
  if (t === 'log_notes' || step?.nodeType === 'log_notes') return 'fa fa-clipboard-list';
  return 'fa fa-bolt';
};

const getStepThemeClass = (step) => {
  const t = step?.type || step?.nodeType || '';
  if (t === 'email_sent' || t.includes('email')) return 'theme-email';
  if (t.includes('instagram')) return 'theme-instagram';
  if (t.includes('linkedin')) return 'theme-linkedin';
  if (t.includes('whatsapp')) return 'theme-whatsapp';
  if (t.includes('phone') || t.includes('call')) return 'theme-phone';
  return 'theme-default';
};

const STEP_LABELS = {
  email_sent: 'Send Email', instagram_dm: 'Send Instagram DM', instagram_follow: 'Follow on Instagram',
  linkedin_message: 'Send LinkedIn Message', linkedin_connect: 'LinkedIn Connect',
  whatsapp: 'Send WhatsApp', delay: 'Wait', condition: 'Decision', end: 'End',
  log_notes: 'Log Notes',
};

const getStepLabel = (step, idx) => {
  const t = step?.type || step?.nodeType || '';
  return STEP_LABELS[t] || step?.note || step?.label || `Step ${idx + 1}`;
};

// Steps that are not shown as touchpoints in the mini roadmap.
const HIDDEN_ROADMAP_TYPES = ['trigger', 'end', 'tag', 'delay'];

const FlowOption = ({ flow, onPick, disabled, prior }) => {
  const allSteps = flow.steps || [];
  const previewSteps = allSteps.filter((s) => {
    const nt = s?.nodeType || s?.type || '';
    return !HIDDEN_ROADMAP_TYPES.includes(nt);
  });
  const shownSteps = previewSteps.slice(0, 5);
  const extraSteps = previewSteps.length - shownSteps.length;
  const totalDays = allSteps.reduce((sum, s) => {
    const isDelay = s?.nodeType === 'delay' || s?.type === 'delay';
    return isDelay ? sum + (parseInt(s.delayDays, 10) || 0) : sum;
  }, 0);

  const flowType = flow.flowType || (flow.trigger === 'manual' ? 'warmup' : null);

  return (
    <button
      className={`fx-flow-option ${prior ? 'has-history' : ''}`}
      onClick={() => !disabled && onPick(flow)}
      disabled={disabled}
    >
      <span className="fx-flow-option-icon">
        <i className={disabled ? 'fa fa-spinner fa-spin' : 'fa fa-rocket'}></i>
        <span className="fx-flow-option-icon-ring"></span>
      </span>
      <div className="fx-flow-option-body">
        <div className="fx-flow-option-top">
          <span className="fx-flow-option-name">{flow.name}</span>
          {flowType && (
            <span className={`fx-flow-type-badge ${flowType}`}>
              <i className={`fa ${flowType === 'warmup' ? 'fa-fire' : 'fa-redo'}`}></i>
              {flowType === 'warmup' ? 'Warmup' : 'Retargeting'}
            </span>
          )}
          {prior && (
            <span className="fx-flow-option-warn" title="This contact was already in this flow">
              <i className="fa fa-history"></i> {prior.count > 1 ? `${prior.count}× before` : 'Been here'}
            </span>
          )}
        </div>

        {shownSteps.length > 0 ? (
          <div className="fx-flow-roadmap">
            {shownSteps.map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="fx-flow-connector"></span>}
                <span className={`fx-flow-step ${getStepThemeClass(s)}`} title={getStepLabel(s, i)}>
                  <i className={getStepIcon(s)}></i>
                </span>
              </React.Fragment>
            ))}
            {extraSteps > 0 && (
              <>
                <span className="fx-flow-connector"></span>
                <span className="fx-flow-step fx-flow-step--more">+{extraSteps}</span>
              </>
            )}
          </div>
        ) : <div className="fx-flow-option-empty">No steps configured yet</div>}

        <div className="fx-flow-option-meta">
          <span className="fx-flow-meta-item">
            <i className="fa fa-layer-group"></i> {allSteps.length} {allSteps.length === 1 ? 'step' : 'steps'}
          </span>
          {totalDays > 0 && (
            <span className="fx-flow-meta-item">
              <i className="fa fa-clock"></i> ~{totalDays} {totalDays === 1 ? 'day' : 'days'}
            </span>
          )}
        </div>
      </div>
      <span className="fx-flow-option-go">
        <i className="fa fa-arrow-right"></i>
        <span className="fx-flow-option-go-label">Start</span>
      </span>
    </button>
  );
};

/**
 * Rich flow picker list, shared by the single-contact modal and the bulk modal.
 *
 * @param {Array}    flows     - Flow definitions to offer.
 * @param {Function} onPick    - Called with the chosen flow object.
 * @param {boolean}  disabled  - Disables all options (e.g. while starting).
 * @param {Function} getPrior  - Optional (flowId) => { count } | null for history badge.
 * @param {string}   emptyText - Text shown when there are no flows.
 */
const FlowPickerList = ({ flows = [], onPick, disabled = false, getPrior, emptyText = 'No flows configured yet' }) => {
  if (!flows.length) {
    return (
      <div className="fx-empty"><i className="fa fa-info-circle"></i><span>{emptyText}</span></div>
    );
  }
  return (
    <div className="fx-flow-picker">
      {flows.map((flow) => (
        <FlowOption
          key={flow.id}
          flow={flow}
          onPick={onPick}
          disabled={disabled}
          prior={getPrior ? getPrior(flow.id) : null}
        />
      ))}
    </div>
  );
};

export default FlowPickerList;
